import { useCallback, useEffect, useState } from "react"

/**
 * OKS-668 — PWA install detection for the mobile "Add to Home Screen" tutorial.
 *
 * All browser access is deferred to `useEffect` / callbacks (never module-eval),
 * so the hook is SSR-safe (`window`/`navigator` are undefined during Vite SSR)
 * and passes the `local-rules/no-module-eval-browser-api` lint gate.
 *
 * Responsibilities:
 *   - detect the platform branch the drawer should render (`ios` | `android` |
 *     `generic`),
 *   - detect whether the app already runs standalone (installed) so the drawer
 *     never shows to an already-installed creator,
 *   - capture Chromium's `beforeinstallprompt` event so the Android branch can
 *     invoke the native install prompt on demand,
 *   - flag iOS-non-Safari so the drawer can add a "do this from Safari" note.
 *
 * The server contributes only the coarse `home_screen_prompt_seen` boolean; this
 * hook supplies every gate the server can't know (viewport is handled separately
 * by `useIsMobile`).
 */

/** Chromium-only install prompt event (not in the standard lib DOM types). */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  prompt: () => Promise<void>
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed"
    platform: string
  }>
}

export type PwaPlatform = "android" | "ios" | "generic"

export interface PwaInstallState {
  /**
   * False until the first client effect has read the browser-only signals
   * (standalone / platform / UA). Consumers gate rendering on this so an
   * already-installed creator never sees a one-frame flash of the drawer
   * before standalone detection resolves — and, since it flips false→true
   * after mount, it doubles as the trigger that drives the drawer's open
   * (slide-up) transition.
   */
  isReady: boolean
  /** Coarse platform branch for the drawer body. */
  platform: PwaPlatform
  /** True when the app is already installed / running standalone. */
  isStandalone: boolean
  /** True on iOS but NOT in Safari (Chrome/Firefox/etc. can't install). */
  isIosNonSafari: boolean
  /**
   * True when a `beforeinstallprompt` event has been captured — i.e. the
   * Android native install CTA can be shown. When false on Android, the drawer
   * falls back to the generic illustrated steps.
   */
  canPromptInstall: boolean
  /**
   * Invokes the captured native install prompt (Android/Chromium). Resolves
   * after the OS sheet is dismissed (accepted or not). No-op when there is no
   * captured prompt. The caller stamps "seen" regardless of outcome.
   */
  promptInstall: () => Promise<void>
}

// Module-scoped singleton for the deferred prompt. `beforeinstallprompt` may
// fire before React mounts; a listener registered once (from the first hook
// effect, browser-only) stashes the event here so a later-mounting drawer can
// still reach it. Assignment happens only inside the browser-only effect.
let deferredPrompt: BeforeInstallPromptEvent | null = null
let listenerRegistered = false
const subscribers = new Set<() => void>()

function notifySubscribers(): void {
  subscribers.forEach((cb) => cb())
}

function ensureInstallListener(): void {
  if (listenerRegistered || typeof window === "undefined") return
  listenerRegistered = true

  window.addEventListener("beforeinstallprompt", (event) => {
    // Prevent Chrome's mini-infobar so we own the install moment.
    event.preventDefault()
    deferredPrompt = event as BeforeInstallPromptEvent
    notifySubscribers()
  })

  // Once installed, drop any stashed prompt so the drawer can't re-offer it.
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null
    notifySubscribers()
  })
}

function detectIsStandalone(): boolean {
  if (typeof window === "undefined") return false
  const mediaStandalone =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches
  // iOS Safari exposes the legacy `navigator.standalone` boolean instead.
  const iosStandalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  return mediaStandalone || iosStandalone
}

function detectPlatform(): PwaPlatform {
  if (typeof navigator === "undefined") return "generic"
  const ua = navigator.userAgent
  if (/iphone|ipad|ipod/i.test(ua)) return "ios"
  if (/android/i.test(ua)) return "android"
  return "generic"
}

function detectIsIosNonSafari(): boolean {
  if (typeof navigator === "undefined") return false
  const ua = navigator.userAgent
  const isIos = /iphone|ipad|ipod/i.test(ua)
  if (!isIos) return false
  // In-app / non-Safari iOS browsers carry CriOS (Chrome), FxiOS (Firefox),
  // EdgiOS (Edge), OPiOS (Opera). Real Safari carries none of these.
  return /crios|fxios|edgios|opios|mercury/i.test(ua)
}

export function usePwaInstall(): PwaInstallState {
  const [isReady, setIsReady] = useState(false)
  const [canPromptInstall, setCanPromptInstall] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false)
  const [platform, setPlatform] = useState<PwaPlatform>("generic")
  const [isIosNonSafari, setIsIosNonSafari] = useState(false)

  useEffect(() => {
    ensureInstallListener()

    // Read the browser-only signals now that we're mounted client-side.
    setIsStandalone(detectIsStandalone())
    setPlatform(detectPlatform())
    setIsIosNonSafari(detectIsIosNonSafari())
    setCanPromptInstall(deferredPrompt !== null)
    setIsReady(true)

    // Subscribe so a `beforeinstallprompt` firing after mount updates the CTA.
    const sync = () => setCanPromptInstall(deferredPrompt !== null)
    subscribers.add(sync)
    return () => {
      subscribers.delete(sync)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    const event = deferredPrompt
    if (!event) return
    try {
      await event.prompt()
      await event.userChoice
    } finally {
      // A prompt can be used once — drop it either way.
      deferredPrompt = null
      notifySubscribers()
    }
  }, [])

  return {
    isReady,
    platform,
    isStandalone,
    isIosNonSafari,
    canPromptInstall,
    promptInstall,
  }
}
